var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { Keys, classNames, guid, useRtl, getTabIndex, dispatchEvent } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { messages, checkboxValidation } from './../messages';
/**
 * Represents the [KendoReact Checkbox component]({% slug overview_checkbox %}).
 *
 * @example
 * ```jsx
 * class App extends React.Component {
 *     render() {
 *         return <Checkbox />;
 *     }
 * }
 * ReactDOM.render(<App />, document.querySelector('my-app'));
 * ```
 */
export var Checkbox = React.forwardRef(function (props, target) {
    var checked = props.checked, className = props.className, children = props.children, defaultChecked = props.defaultChecked, disabled = props.disabled, id = props.id, label = props.label, labelPlacement = props.labelPlacement, onChange = props.onChange, onFocus = props.onFocus, tabIndex = props.tabIndex, value = props.value, required = props.required, validationMessage = props.validationMessage, validityStyles = props.validityStyles;
    var elementRef = React.useRef(null);
    var focusElement = React.useCallback(function () {
        if (elementRef.current) {
            elementRef.current.focus();
        }
    }, []);
    React.useImperativeHandle(target, function () { return ({
        element: elementRef,
        focus: focusElement
    }); });
    var _a = React.useState(defaultChecked), stateChecked = _a[0], setStateChecked = _a[1];
    var valueIsBoolean = (typeof value === 'boolean' || value === null);
    var isControlled = checked !== undefined || valueIsBoolean;
    var currentChecked = isControlled ? checked : stateChecked;
    var useValueAsChecked = currentChecked === undefined && valueIsBoolean;
    var checkedProp = useValueAsChecked ? value : currentChecked;
    var valueProp = (useValueAsChecked || valueIsBoolean) ? undefined : value;
    var indeterminateProp = checkedProp === null;
    var calculatedId = React.useMemo(function () { return guid(); }, []);
    var localizationService = useLocalization();
    var localizeMessage = function (message) {
        return localizationService.toLanguageString(message, messages[message]);
    };
    var defaultValidationMessage = localizeMessage(checkboxValidation);
    var isValid = props.valid !== undefined ?
        props.valid :
        !required ?
            true :
            currentChecked ? true : false;
    React.useEffect(function () {
        if (elementRef.current && elementRef.current.setCustomValidity) {
            elementRef.current.setCustomValidity(isValid
                ? ''
                : (validationMessage || defaultValidationMessage));
        }
    }, [isValid, validationMessage, defaultValidationMessage]);
    var setValue = React.useCallback(function (e, val) {
        if (!isControlled) {
            setStateChecked(val);
        }
        if (onChange) {
            var handle = {
                element: elementRef,
                focus: focusElement
            };
            dispatchEvent(onChange, e, handle, { value: val });
        }
    }, [onChange, setStateChecked]);
    var onChangeHandler = React.useCallback(function (e) {
        var newValue = e.target.checked;
        setValue(e, newValue);
    }, [setValue]);
    var onKeyDownHandler = React.useCallback(function (e) {
        if (disabled) {
            return;
        }
        var keyCode = e.keyCode;
        var currentValue = e.currentTarget.checked;
        if (keyCode === Keys.space || keyCode === Keys.enter) {
            e.preventDefault();
            e.stopPropagation();
            setValue(e, !currentValue);
        }
    }, [setValue]);
    var onFocusHandler = React.useCallback(function (e) {
        if (onFocus) {
            var handle = {
                element: elementRef,
                focus: focusElement
            };
            dispatchEvent(onFocus, e, handle, undefined);
        }
    }, [onFocus]);
    var dir = useRtl(elementRef, props.dir);
    var checkboxClasses = classNames({
        'k-state-default': !disabled,
        'k-state-disabled': disabled
    }, className);
    var inputProps = {
        type: 'checkbox',
        className: classNames({
            'k-checkbox': true,
            'k-state-indeterminate': indeterminateProp,
            'k-state-invalid': !(isValid || validityStyles !== undefined || validityStyles === true)
        }),
        ref: elementRef,
        id: id || calculatedId,
        checked: Boolean(checkedProp),
        disabled: disabled,
        tabIndex: getTabIndex(tabIndex, disabled),
        role: 'checkbox',
        required: required !== undefined ? required : false,
        'aria-checked': currentChecked || checkedProp ?
            true :
            indeterminateProp ?
                'mixed' :
                false,
        'aria-disabled': disabled || undefined,
        onChange: onChangeHandler,
        onKeyDown: onKeyDownHandler,
        onFocus: onFocusHandler
    };
    var checkboxInput = (React.createElement(React.Fragment, null, 
    // removing value prop is required due to bug in react where
    // value set to undefined override default submit value
    valueProp === undefined ? (React.createElement("input", __assign({}, inputProps))) : (React.createElement("input", __assign({}, inputProps, { value: valueIsBoolean ? undefined : valueProp })))));
    var checkboxLabel = (React.createElement(React.Fragment, null, label !== undefined ? (React.createElement("label", { className: 'k-checkbox-label', htmlFor: id || calculatedId, style: { userSelect: 'none' } }, label)) : null));
    return (React.createElement(React.Fragment, null, (labelPlacement === 'before') ? (React.createElement("span", { className: checkboxClasses, dir: 'rtl' },
        checkboxInput,
        checkboxLabel,
        children)) : (React.createElement("span", { className: checkboxClasses, dir: dir },
        checkboxInput,
        checkboxLabel,
        children))));
});
Checkbox.propTypes = {
    checked: PropTypes.bool,
    className: PropTypes.string,
    defaultChecked: PropTypes.bool,
    defaultValue: PropTypes.any,
    dir: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.string,
    labelPlacement: PropTypes.string,
    tabIndex: PropTypes.number,
    value: PropTypes.any,
    validationMessage: PropTypes.string,
    required: PropTypes.bool,
    validate: PropTypes.bool,
    valid: PropTypes.bool,
    onChange: PropTypes.func,
    onFocus: PropTypes.func
};
Checkbox.displayName = 'KendoCheckbox';
//# sourceMappingURL=Checkbox.js.map